import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Clock, Info, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useEntitlements } from './useEntitlements';
import { apiClient } from '../../api/client';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatTranslation } from '../../utils/translations';
import { Button } from '../../components/common';

interface WatchItemSummary {
  id: string;
  product_title?: string | null;
  domain?: string | null;
  created_at?: string | null;
  is_active?: boolean;
}

export const RetentionSelector: React.FC = () => {
  const { entitlements, saveRetention, isSavingRetention, saveRetentionError } =
    useEntitlements();
  const { t } = useLanguage();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [initialLoaded, setInitialLoaded] = useState(false);

  // Fetch active watches to allow customer selection
  const { data: watchData, isLoading: isLoadingWatches } = useQuery({
    queryKey: ['watches', 'active-for-retention'],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/api/v1/watches', {
        params: { query: { limit: 100, offset: 0 } },
      });
      if (error || !data) throw error || new Error('Failed to load watches');
      return data;
    },
    enabled: Boolean(entitlements?.pending_package_code),
  });

  const activeWatches: WatchItemSummary[] = useMemo(() => {
    if (!watchData?.items) return [];
    return (watchData.items as WatchItemSummary[])
      .filter((w) => Boolean(w.is_active))
      .sort((a, b) => {
        const da = a.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b.created_at ? new Date(b.created_at).getTime() : 0;
        return da - db; // Oldest first
      });
  }, [watchData]);

  const targetLimit = entitlements?.activation_limit ?? 10;
  const isDowngrading = Boolean(
    entitlements?.pending_package_code &&
      (entitlements.active_count > targetLimit ||
        (entitlements.retained_watch_ids && entitlements.retained_watch_ids.length > 0))
  );

  // Initialize selected IDs: use server-persisted retained_watch_ids if available,
  // or default to oldest-first up to target limit.
  useEffect(() => {
    if (!initialLoaded && activeWatches.length > 0 && entitlements) {
      if (Array.isArray(entitlements.retained_watch_ids)) {
        setSelectedIds(entitlements.retained_watch_ids);
      } else {
        const defaultOldest = activeWatches.slice(0, targetLimit).map((w) => w.id);
        setSelectedIds(defaultOldest);
      }
      setInitialLoaded(true);
    }
  }, [activeWatches, entitlements, initialLoaded, targetLimit]);

  if (!isDowngrading) {
    return null;
  }

  const handleToggle = (watchId: string) => {
    setSuccessMessage(null);
    setSaveError(null);
    setSelectedIds((prev) => {
      if (prev.includes(watchId)) {
        return prev.filter((id) => id !== watchId);
      }
      if (prev.length >= targetLimit) {
        return prev;
      }
      return [...prev, watchId];
    });
  };

  const handleSave = async () => {
    setSuccessMessage(null);
    setSaveError(null);
    try {
      await saveRetention(selectedIds);
      setSuccessMessage(t('packages.savedSuccess'));
    } catch {
      setSaveError(t('packages.saveError'));
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs space-y-5">
      <div className="border-b border-slate-100 dark:border-slate-800/80 pb-4">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <Clock className="w-4 h-4 text-amber-500" />
          <span>{t('packages.retentionTitle')}</span>
        </h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          {formatTranslation(t('packages.retentionSubtitle'), { limit: targetLimit })}
        </p>
      </div>

      {/* Info Notice about oldest-first and paused history */}
      <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200/60 dark:border-slate-800 text-xs text-slate-600 dark:text-slate-400 space-y-1">
        <div className="flex items-center gap-1.5 font-medium text-slate-700 dark:text-slate-300">
          <Info className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
          <span>{t('packages.oldestFirstNotice')}</span>
        </div>
        <p className="text-[11px] ps-5 leading-relaxed">
          {t('packages.unretainedPausedNotice')}
        </p>
      </div>

      {/* Watch Items Selection List */}
      {isLoadingWatches ? (
        <div className="py-8 text-center text-slate-400 flex flex-col items-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
          <span className="text-xs">{t('packages.loading')}</span>
        </div>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto pe-1">
          {activeWatches.map((watch) => {
            const isSelected = selectedIds.includes(watch.id);
            const isLimitReached = selectedIds.length >= targetLimit && !isSelected;

            return (
              <label
                key={watch.id}
                className={`flex items-center justify-between p-3 rounded-xl border text-xs cursor-pointer transition-all select-none ${
                  isSelected
                    ? 'bg-indigo-50/50 dark:bg-indigo-950/20 border-indigo-300 dark:border-indigo-800 text-slate-900 dark:text-slate-100'
                    : isLimitReached
                    ? 'opacity-50 cursor-not-allowed bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800'
                    : 'bg-white dark:bg-slate-950/40 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  disabled={isLimitReached}
                  onChange={() => !isLimitReached && handleToggle(watch.id)}
                  className="sr-only"
                  aria-label={watch.product_title || 'Untitled Product'}
                />
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div
                    aria-hidden="true"
                    className={`w-4 h-4 rounded-md border flex items-center justify-center transition-colors ${
                      isSelected
                        ? 'bg-indigo-600 border-indigo-600 text-white'
                        : 'border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900'
                    }`}
                  >
                    {isSelected && <Check className="w-3 h-3 stroke-[3]" />}
                  </div>
                  <div className="truncate">
                    <p className="font-medium truncate">
                      {watch.product_title || 'Untitled Product'}
                    </p>
                    <p className="text-[10px] text-slate-400 font-mono">
                      {watch.domain || 'Store'}
                    </p>
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      )}

      {/* Feedback Messages */}
      {successMessage && (
        <div role="status" className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 text-xs flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}

      {(saveError || saveRetentionError) && (
        <div role="alert" className="p-3 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 text-xs flex items-center gap-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{t('packages.saveError')}</span>
        </div>
      )}

      {/* Actions Bar */}
      <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-800/80">
        <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
          {formatTranslation(t('packages.retainedCount'), {
            selected: selectedIds.length,
            limit: targetLimit,
          })}
        </span>
        <Button
          onClick={handleSave}
          disabled={isSavingRetention || isLoadingWatches}
          size="sm"
          className="gap-2"
        >
          {isSavingRetention && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          <span>{isSavingRetention ? t('packages.savingRetention') : t('packages.saveRetention')}</span>
        </Button>
      </div>
    </div>
  );
};
